import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Search, Calculator, TrendingUp, MapPin, Briefcase, Users } from 'lucide-react';
import UserMenu from '@/components/auth/UserMenu';
import WageAtlasLogo from './WageAtlasLogo';
import { ThemeToggle } from '@/components/ui/theme-toggle';
import { Button } from '@/components/ui/button';
import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  NavigationMenuTrigger,
  navigationMenuTriggerStyle,
} from '@/components/ui/navigation-menu';
import { cn } from '@/lib/utils';

const Header: React.FC = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const location = useLocation();
  const isHomePage = location.pathname === '/';

  const headerClasses = cn(
    "fixed top-0 left-0 right-0 z-50 transition-all duration-300",
    isHomePage 
      ? "bg-background/80 backdrop-blur-md border-b border-border/40" 
      : "bg-background/95 backdrop-blur-md border-b border-border shadow-sm"
  );

  const toggleMenu = () => setIsMenuOpen(!isMenuOpen);

  const navigationItems = [
    {
      title: "Explore Careers",
      icon: Briefcase,
      items: [
        { title: "In-Demand Careers", href: "/in-demand-careers/", description: "Occupations with critical shortages and high hiring demand" },
        { title: "AI Economy Jobs", href: "/ai-economy-jobs/", description: "Salaries for the careers powering the AI economy" },
        { title: "No-Degree Jobs", href: "/no-degree-jobs/", description: "High-paying jobs that don't require a degree" },
        { title: "Entry-Level Jobs", href: "/best-paying-entry-level/", description: "Best paying jobs for career starters" },
        { title: "Best States", href: "/best-states/", description: "Find the best paying states for any occupation" },
      ]
    },
    {
      title: "Salary Data", 
      icon: TrendingUp,
      items: [
        { title: "Search Occupations", href: "/search/", description: "Search and compare salary data across occupations" },
        { title: "Occupation Rankings", href: "/rankings/", description: "Rank all occupations by salary, employment, and SOC code" },
        { title: "Industry Center", href: "/industry/", description: "Explore salary trends by industry sector" },
        { title: "Wage Trends", href: "/trends/", description: "Historical wage data and trend analysis" },
        { title: "Real Wages Explained", href: "/real-wages/", description: "What your salary is worth after cost of living" },
      ]
    },
    {
      title: "Tools",
      icon: Calculator,
      items: [
        { title: "Am I Underpaid?", href: "/am-i-underpaid/", description: "Check how your salary compares to market rates" },
        { title: "Income Percentile", href: "/income-percentile/", description: "See what percent of Americans earn your salary" },
        { title: "Salary Calculator", href: "/calculators/salary/", description: "Convert between hourly, monthly, and annual pay" },
        { title: "Tax Calculator", href: "/calculators/tax/", description: "Estimate your take-home pay after taxes" },
        { title: "AI Career Analysis", href: "/ai-career-analysis/", description: "AI-powered career automation risk assessment" },
      ]
    }
  ];

  return (
    <header className={headerClasses}>
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center">
            <WageAtlasLogo size="md" />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center space-x-6">
            <NavigationMenu>
              <NavigationMenuList>
                {navigationItems.map((section) => (
                  <NavigationMenuItem key={section.title}>
                    <NavigationMenuTrigger className={cn(navigationMenuTriggerStyle(), "bg-transparent")}>
                      <section.icon className="w-4 h-4 mr-2" />
                      {section.title}
                    </NavigationMenuTrigger>
                    <NavigationMenuContent>
                      <div className="grid gap-3 p-6 w-[400px]">
                        <div className="row-span-3">
                          <NavigationMenuLink asChild>
                            <div className="flex h-full w-full select-none flex-col justify-end rounded-md bg-gradient-primary p-6 no-underline outline-none focus:shadow-md">
                              <section.icon className="h-6 w-6 text-white" />
                              <div className="mb-2 mt-4 text-lg font-medium text-white">
                                {section.title}
                              </div>
                              <p className="text-sm leading-tight text-white/90">
                                Discover comprehensive salary data and career insights
                              </p>
                            </div>
                          </NavigationMenuLink>
                        </div>
                        {section.items.map((item) => (
                          <NavigationMenuLink key={item.title} asChild>
                            <Link
                              to={item.href}
                              className="group block select-none space-y-1 rounded-md p-3 leading-none no-underline outline-none transition-colors hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground"
                            >
                              <div className="text-sm font-medium leading-none">{item.title}</div>
                              <p className="line-clamp-2 text-sm leading-snug text-muted-foreground group-hover:text-accent-foreground/80 group-focus:text-accent-foreground/80">
                                {item.description}
                              </p>
                            </Link>
                          </NavigationMenuLink>
                        ))}
                      </div>
                    </NavigationMenuContent>
                  </NavigationMenuItem>
                ))}
              </NavigationMenuList>
            </NavigationMenu>
          </div>

          {/* Desktop Actions */}
          <div className="hidden lg:flex items-center space-x-3">
            <Link to="/search/">
              <Button variant="outline" size="sm" className="border-border/40">
                <Search className="w-4 h-4 mr-2" />
                Search
              </Button>
            </Link>
            <ThemeToggle />
            <UserMenu />
          </div>

          {/* Mobile Menu Button */}
          <div className="lg:hidden flex items-center space-x-2">
            <ThemeToggle />
            <Button variant="outline" size="icon" onClick={toggleMenu} className="border-border/40">
              {isMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </Button>
          </div>
        </div>

        {/* Mobile Menu */}
        {isMenuOpen && (
          <div className="lg:hidden border-t border-border/40 bg-background/95 backdrop-blur-md">
            <div className="px-4 py-6 space-y-4">
              {/* Search */}
              <Link 
                to="/search/" 
                className="flex items-center px-3 py-2 rounded-md text-foreground hover:bg-accent hover:text-accent-foreground transition-colors"
                onClick={() => setIsMenuOpen(false)}
              >
                <Search className="w-4 h-4 mr-3" />
                Search Occupations
              </Link>

              {/* Navigation Sections */}
              {navigationItems.map((section) => (
                <div key={section.title} className="space-y-2">
                  <h3 className="flex items-center text-sm font-semibold text-foreground px-3">
                    <section.icon className="w-4 h-4 mr-2" />
                    {section.title}
                  </h3>
                  {section.items.map((item) => (
                    <Link
                      key={item.title}
                      to={item.href}
                      className="block px-6 py-2 text-sm text-muted-foreground hover:text-accent-foreground hover:bg-accent rounded-md transition-colors"
                      onClick={() => setIsMenuOpen(false)}
                    >
                      {item.title}
                    </Link>
                  ))}
                </div>
              ))}

              {/* User Menu */}
              <div className="pt-4 border-t border-border/40">
                <UserMenu />
              </div>
            </div>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;